/**
 * The user's categories (name + colour, stored by the platform API) and the category filter of the grid. Events keep
 * only the names (Outlook categories), so names Outlook added without a colour here are shown without one.
 */
import { toast } from '@platform/ui';
import { useCallback, useEffect, useState } from 'react';
import { api } from '../api';
import type { Category } from '../api/calendar-extra';

export function useCategories(active: boolean, failedMessage: string) {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loaded, setLoaded] = useState(false);
  /** Names whose events are shown; empty = no filter. */
  const [filter, setFilter] = useState<ReadonlySet<string>>(new Set());

  useEffect(() => {
    if (!active || loaded) return;
    let cancelled = false;
    api.calendarCategories().then(
      (list) => {
        if (cancelled) return;
        setCategories(list);
        setLoaded(true);
      },
      () => undefined,
    );
    return () => {
      cancelled = true;
    };
  }, [active, loaded]);

  const save = useCallback(
    (next: readonly Category[]) => {
      setCategories([...next]);
      api.setCalendarCategories(next).catch(() => toast.error(failedMessage));
    },
    [failedMessage],
  );
  const toggleFilter = useCallback((name: string) => {
    setFilter((current) => {
      const next = new Set(current);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }, []);

  return { categories, saveCategories: save, categoryFilter: filter, toggleCategoryFilter: toggleFilter };
}

/** Category of a name (case-insensitive, like Outlook). */
export const categoryOf = (categories: readonly Category[], name: string): Category | undefined => categories.find((c) => c.name.toLowerCase() === name.toLowerCase());
